import { useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  DataTable,
  SectionCard,
  type ColumnDef,
  PageWrapper,
} from "@/deps/ui-library";
import {
  useCloneTemplate,
  useDeleteTemplate,
  useReportFrameworks,
  useReportTemplates,
  useSubscriptionStatus,
} from "@/api/queries";
import type { ReportTemplateSummary } from "@/api/types";
import { useCan } from "@/app/session";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";

/** The feature's code in RigoSub's catalogue (ADR-0061). */
const FEATURE = "REPORTING";

/** The supplied formats, read-only, and the company's own copies (ADR-0061). */
export function ReportTemplatesPage() {
  const subscription = useSubscriptionStatus();
  const features = subscription.data?.features;
  const entitled = !features || features.includes(FEATURE);
  const { data, isPending, error } = useReportTemplates(entitled);
  const { data: frameworks } = useReportFrameworks(entitled);
  const can = useCan();
  const editable = can("reports.layout.edit");
  const navigate = useNavigate();
  const clone = useCloneTemplate();
  const remove = useDeleteTemplate();

  const columns = useMemo<Array<ColumnDef<ReportTemplateSummary>>>(
    () => [
      {
        id: "name",
        header: "Format",
        cell: ({ row: { original } }) => (
          <Link
            to="/reports/templates/$code"
            params={{ code: original.code }}
            className="text-primary underline"
          >
            {original.name}
          </Link>
        ),
      },
      {
        id: "framework",
        header: "Framework",
        cell: ({ row: { original } }) =>
          frameworks?.find((f) => f.code === original.frameworkCode)?.name ??
          original.frameworkCode,
      },
      {
        id: "statement",
        header: "Statement",
        cell: ({ row: { original } }) =>
          original.statement === "pl" ? "Profit and loss" : "Balance sheet",
      },
      { id: "version", header: "Version", cell: ({ row }) => row.original.version },
      {
        id: "kind",
        header: "",
        cell: ({ row: { original } }) => (
          <Badge colorScheme={original.supplied ? "default" : "success"} size="sm">
            {original.supplied ? "Supplied" : "Company"}
          </Badge>
        ),
      },
      ...(editable
        ? [
            {
              id: "actions",
              header: "",
              meta: { textAlign: "right" as const },
              cell: ({ row: { original } }: { row: { original: ReportTemplateSummary } }) => (
                <span className="flex justify-end gap-1">
                  <Button
                    colorScheme="default"
                    variant="ghost"
                    size="xs"
                    aria-label={`Clone ${original.name}`}
                    disabled={clone.isPending}
                    onClick={() =>
                      clone.mutate(original.code, {
                        onSuccess: (made) =>
                          void navigate({
                            to: "/reports/templates/$code",
                            params: { code: made.summary.code },
                          }),
                      })
                    }
                  >
                    Clone
                  </Button>
                  {original.supplied ? null : (
                    <Button
                      colorScheme="default"
                      variant="ghost"
                      size="xs"
                      aria-label={`Delete ${original.name}`}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(original.code)}
                    >
                      Delete
                    </Button>
                  )}
                </span>
              ),
            },
          ]
        : []),
    ],
    [clone, editable, frameworks, navigate, remove],
  );

  if (!entitled) {
    return (
      <EmptyState
        icon="Lock"
        title="Report templates come with the Reporting layer add-on"
        description="Statements in the standard formats stay available. An owner or admin can add it to the subscription."
      />
    );
  }

  const failure = error ?? clone.error ?? remove.error;

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title="Report templates"
        subtitle="A supplied format stays as it is. Clone one to change its lines."
      />
      {failure ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(failure as Error).message}
        />
      ) : null}
      <SectionCard title="Formats" flush>
        <DataTable
          aria-label="Report templates"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={data ?? []}
          loading={isPending}
          getRowId={(row) => row.code}
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
      </SectionCard>
      <p className="text-fg-muted text-xs">Supplied format · not a compliance assertion</p>
    </PageWrapper>
  );
}
